import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 20;
      setIsScrolled(scrolled);

      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled_percent = (winScroll / height) * 100;
      setScrollProgress(scrolled_percent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Zoho', path: '/zoho' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <>
      <div 
        className="fixed top-0 left-0 h-0.5 bg-gradient-to-r from-[#11AAB6] to-[#052E6C] z-[60] transition-all duration-300"
        style={{ width: `${scrollProgress}%` }}
      />
      
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-xl shadow-sm' 
            : 'bg-white'
        }`}
        style={{
          borderBottom: isScrolled ? '1px solid rgba(5, 46, 108, 0.12)' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group" data-testid="navbar-logo-link">
              <img
                src="/logo-light.webp"
                alt="ImpacTech Digital"
                className="h-11 w-auto transform transition-transform group-hover:scale-105"
                data-testid="navbar-logo-img"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-['Inter'] font-medium text-sm transition-colors relative group`}
                  style={{
                    color: location.pathname === link.path ? '#11AAB6' : 'rgba(5, 46, 108, 0.55)'
                  }}
                >
                  {link.name}
                  <span 
                    className={`absolute -bottom-1 left-0 h-0.5 bg-[#11AAB6] transition-all ${
                      location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                    }`} 
                  />
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Button 
                asChild
                className="bg-[#11AAB6] hover:bg-[#0E8A94] text-white px-6 py-2.5 rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
                  Free Consultation
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[#052E6C] rounded-lg transition-colors"
              style={{
                backgroundColor: isMobileMenuOpen ? 'rgba(17, 170, 182, 0.12)' : 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(17, 170, 182, 0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isMobileMenuOpen ? 'rgba(17, 170, 182, 0.12)' : 'transparent'}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu — rendered outside <nav> because its backdrop-blur creates a
          containing block that breaks fixed positioning of descendants */}
      <div
        className={`md:hidden fixed inset-0 top-20 z-[70] transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: '#FFFFFF', height: 'calc(100dvh - 5rem)' }}
        data-testid="mobile-menu-overlay"
      >
        <div className="flex flex-col p-6 space-y-4 h-full overflow-y-auto" style={{ backgroundColor: '#FFFFFF' }}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`font-['Inter'] font-medium text-lg py-3 px-4 rounded-xl transition-colors`}
              style={{
                backgroundColor: location.pathname === link.path ? 'rgba(17, 170, 182, 0.12)' : 'transparent',
                color: location.pathname === link.path ? '#11AAB6' : 'rgba(5, 46, 108, 0.7)'
              }}
              data-testid={`mobile-menu-link-${link.name.toLowerCase()}`}
            >
              {link.name}
            </Link>
          ))}
          <Button 
            asChild
            className="bg-[#11AAB6] hover:bg-[#0E8A94] text-white w-full py-3 rounded-full font-['Inter'] font-medium mt-4"
          >
            <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
              Free Consultation
            </a>
          </Button>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-20" />
    </>
  );
};

export default Navbar;
